import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is a helpdesk without per-seat pricing?',
    a: 'It is a helpdesk where the bill does not grow with the number of agents. The usual alternatives are usage-based pricing (you pay per ticket), a flat plan, or self-hosting. Kora is usage-based: $0.10 per ticket, with unlimited members and no monthly plan.',
  },
  {
    q: 'How does pay-per-ticket pricing work in Kora?',
    a: '$1 buys 10 credits and each ticket created costs 1 credit, which is $0.10. Comments, resolving, SLA tracking, automation rules and AI ticket structuring are free. An AI Analyst answer costs 1 credit. New workspaces start with 1,100 free credits and no card.',
  },
  {
    q: 'Is per-ticket pricing always cheaper than per-seat?',
    a: 'No. At very high ticket volumes per-seat pricing can be cheaper. As a simple example, 10 agents at $50 a seat cost $500 a month, which equals 5,000 tickets at $0.10. Below that volume pay-per-ticket costs less; above it the seat price wins. The cost calculator finds the break-even for your own numbers.',
  },
  {
    q: 'Do I pay for teammates who only need to read tickets?',
    a: 'No. Members are unlimited and free, including viewers, so the people who only need visibility cost nothing.',
  },
  {
    q: 'What is free besides seats?',
    a: 'Comments and attachments, AI ticket structuring, SLA policies and breach alerts, automation rules with dry-run, categories, webhooks, embedded sessions, CSV export and the help center reading experience.',
  },
  {
    q: 'What happens if I run out of credits?',
    a: 'The wallet can overdraw to −10 credits so a customer ticket is not lost. Beyond that, creating tickets and AI Analyst answers pause until you top up from $5. Reading and everything else keep working.',
  },
  {
    q: 'Is there a monthly minimum or a contract?',
    a: 'No. There are no monthly plans and no contracts. You buy credits when you want them, from $5, and purchased credits are valid for 10 years.',
  },
  {
    q: 'How do I check what a per-seat helpdesk would really cost me?',
    a: 'Count every person who needs a login, add any AI or add-on fees, and compare the total with your monthly ticket volume times $0.10. The helpdesk cost calculator does this with your own seat price.',
  },
];

export function HelpdeskWithoutPerSeatPricingPage() {
  useEffect(() => {
    const title = 'Helpdesk Without Per-Seat Pricing: Pay Per Ticket | Kora';
    const description = 'A helpdesk with no per-seat pricing: pay $0.10 per ticket, unlimited members, no monthly plan. See the break-even math and what to check before you choose.';
    setDocumentMeta(title, description);
    setCanonical('/helpdesk-without-per-seat-pricing');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/helpdesk-without-per-seat-pricing', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Helpdesk without per-seat pricing', url: 'https://usekora.app/helpdesk-without-per-seat-pricing' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Pricing guide"
      title="A helpdesk without per-seat pricing"
      subtitle="Pay for the tickets your team handles, not for every login. How the model works, when it is cheaper, and when it is not."
      answer={
        <>
          <strong>Kora is a helpdesk with no per-seat pricing.</strong> You pay $0.10 per ticket created ($1 buys 10 credits),
          members are unlimited and free, and there is no monthly plan. SLA tracking, automation rules and AI ticket structuring
          are included. At very high ticket volumes a per-seat plan can be cheaper, and the calculator shows where.
        </>
      }
      ctaTitle="Try pay-per-ticket with 1,100 free credits"
      ctaSubtitle="No card, no seats, no monthly plan. Live in minutes."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mt-4 mb-3">
        Why per-seat pricing hurts small support teams
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-4">
        Per-seat pricing charges for every person with a login, whether they answer ten tickets a day or one a week. The bill
        grows each time the team does, and people who only need visibility still count as seats. For a small team with uneven
        ticket volume, that means paying for capacity you are not using.
      </p>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-6">
        Four pricing models avoid or soften the problem: usage-based (pay per ticket), flat or tiered plans, per-resolution fees
        for AI, and self-hosting. Each has trade-offs, so compare them on your own numbers.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        How Kora prices it
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300 mb-6 list-disc pl-5">
        <li><strong>$0.10 per ticket created.</strong> $1 buys 10 credits and a ticket costs 1 credit. Resolving or closing is never charged.</li>
        <li><strong>Unlimited members, free.</strong> Agents, managers, viewers: no seat fees.</li>
        <li><strong>Included free:</strong> comments and attachments, AI ticket structuring, SLA tracking, automation rules, categories, webhooks, embedded sessions and CSV export.</li>
        <li><strong>1,100 free credits</strong> to start, no card. Top up from $5 when you need more.</li>
        <li><strong>AI Analyst:</strong> 1 credit per successful answer.</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        The break-even math
      </h2>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 dark:border-zinc-800 my-6">
        <table className="w-full min-w-[480px] text-sm bg-white dark:bg-zinc-900">
          <thead>
            <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/40">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 dark:text-zinc-100">Example (your numbers will differ)</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 dark:text-zinc-100">Monthly cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {[
              ['10 agents at $50 per seat', '$500'],
              ['Kora, 300 tickets at $0.10', '$30'],
              ['Kora, 1,000 tickets at $0.10', '$100'],
              ['Kora, 5,000 tickets at $0.10', '$500 (break-even with the $500 seat plan)'],
            ].map(([a, c]) => (
              <tr key={a}>
                <td className="px-4 py-3 font-medium text-zinc-900 dark:text-zinc-100">{a}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-6">
        The $50 seat price is an example, not a vendor quote. Above the break-even ticket volume, a per-seat plan costs less.
        Enter your own seat price and volume in the{' '}
        <Link to="/helpdesk-cost-calculator" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">helpdesk cost calculator</Link>.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        What to check before you choose any helpdesk
      </h2>
      <ol className="space-y-2 text-sm text-zinc-600 dark:text-zinc-300 mb-6 list-decimal pl-5">
        <li>Who counts as a seat: only agents, or also managers, viewers and part-time helpers?</li>
        <li>What AI costs: included, an add-on per agent, or a fee per resolution.</li>
        <li>Which features sit behind higher tiers: SLA tracking, automation, API access.</li>
        <li>Annual commitment versus pay as you go, and what happens if you shrink the team.</li>
        <li>Your real monthly ticket volume, not the volume you hope for.</li>
      </ol>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-3">
        When per-seat is the better fit
      </h2>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed mb-6">
        If a small team handles a very large ticket volume, a flat seat price can win. Kora is also not built for every channel
        yet: email-to-ticket, live chat and a Slack integration are on the{' '}
        <Link to="/roadmap" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">roadmap</Link>, not available today.
        If you need them now, choose a tool that has them.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 dark:text-zinc-50 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 dark:divide-zinc-800 border-y border-zinc-100 dark:border-zinc-800 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 dark:border-blue-500/20 bg-blue-50/50 dark:bg-blue-500/5 p-5">
        <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
          Related: <Link to="/pay-as-you-go-helpdesk" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">how usage-based pricing works</Link>,{' '}
          <Link to="/compare" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">compare Kora with other helpdesks</Link>,{' '}
          <Link to="/pricing" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">Kora pricing</Link>.
        </p>
      </div>
    </ContentPage>
  );
}
